import { useEffect, useCallback, useRef } from "react";
import { driver } from "driver.js";
import "driver.js/dist/driver.css";
import { useProLanguage, PRO_TRANSLATIONS } from "../global/ProLanguageContext";

const TOUR_STORAGE_KEY = "pro-tour-completed";

/**
 * Guided tour for the full Pro CMS.
 *
 * Tour flow:
 *  1. Welcome (centered popover)
 *  2. Workspace & Location in top bar
 *  3. Sidebar overview
 *  4. Screens — pair your first display
 *  5. Media — upload & manage files
 *  6. Playlists — build content loops from media
 *  7. Campaigns — bundle playlists and assign to screens
 *  8. Widgets — dynamic data elements
 *  9. Events — event-triggered content
 * 10. Help button
 * 11. Finish
 */
const AppTour = ({ isReady }) => {
  const { lang } = useProLanguage();
  const t = (PRO_TRANSLATIONS[lang] || PRO_TRANSLATIONS.en).appTour;
  const driverRef = useRef(null);

  const buildSteps = useCallback(() => {
    const steps = [];

    // ── Step 1: Welcome ──
    steps.push({
      popover: {
        title: t.welcomeTitle,
        description: t.welcomeDesc,
        side: "over",
        align: "center",
      },
    });

    // ── Step 2: Workspace & Location in top bar ──
    steps.push({
      element: "#pro-topbar-workspace",
      popover: {
        title: t.workspaceTitle,
        description: t.workspaceDesc,
        side: "bottom",
        align: "center",
      },
    });

    // ── Step 3: Sidebar ──
    steps.push({
      element: "#pro-sidemenu",
      popover: {
        title: t.navTitle,
        description: t.navDesc,
        side: "right",
        align: "center",
      },
    });

    // ── Step 4: Screens ──
    steps.push({
      element: "#pro-nav-screen",
      popover: {
        title: t.screenTitle,
        description: t.screenDesc,
        side: "right",
        align: "center",
      },
    });

    // ── Step 5: Media ──
    steps.push({
      element: "#pro-nav-media",
      popover: {
        title: t.mediaTitle,
        description: t.mediaDesc,
        side: "right",
        align: "center",
      },
    });

    // ── Step 6: Playlists ──
    steps.push({
      element: "#pro-nav-playlist",
      popover: {
        title: t.playlistTitle,
        description: t.playlistDesc,
        side: "right",
        align: "center",
      },
    });

    // ── Step 7: Campaigns ──
    steps.push({
      element: "#pro-nav-campaign",
      popover: {
        title: t.campaignTitle,
        description: t.campaignDesc,
        side: "right",
        align: "center",
      },
    });

    // ── Step 8: Events ──
    steps.push({
      element: "#pro-nav-event",
      popover: {
        title: t.eventTitle,
        description: t.eventDesc,
        side: "right",
        align: "center",
      },
    });

    // ── Step 9: Widgets ──
    steps.push({
      element: "#pro-nav-widget",
      popover: {
        title: t.widgetTitle,
        description: t.widgetDesc,
        side: "right",
        align: "center",
      },
    });

    // ── Step 10: Help button in sidebar ──
    steps.push({
      element: "#pro-sidemenu-help-btn",
      popover: {
        title: t.helpTitle,
        description: t.helpDesc,
        side: "right",
        align: "center",
      },
    });

    // ── Step 11: Finish ──
    steps.push({
      popover: {
        title: t.finishTitle,
        description: t.finishDesc,
        side: "over",
        align: "center",
      },
    });

    return steps;
  }, [t]);

  const startTour = useCallback(() => {
    if (driverRef.current) {
      driverRef.current.destroy();
    }

    // Same fixArrow approach as GuidedTour — compute arrow position from
    // actual bounding rects so it always points at the element's centre.
    const fixArrow = () => {
      const arrow = document.querySelector(
        "#driver-popover-content .driver-popover-arrow",
      );
      if (!arrow || arrow.classList.contains("driver-popover-arrow-none"))
        return;

      arrow.style.setProperty("border-color", "transparent", "important");

      const popover = document.getElementById("driver-popover-content");
      const activeEl = document.querySelector(".driver-active-element");
      if (!popover || !activeEl) return;

      const pr = popover.getBoundingClientRect();
      const er = activeEl.getBoundingClientRect();

      if (arrow.classList.contains("driver-popover-arrow-side-right")) {
        arrow.style.setProperty("border-right-color", "#ffffff", "important");
        const top = Math.max(
          12,
          Math.min(pr.height - 22, er.top + er.height / 2 - pr.top - 5),
        );
        arrow.style.setProperty("top", `${top}px`, "important");
        arrow.style.setProperty("bottom", "auto", "important");
        arrow.style.setProperty("margin-top", "0", "important");
      } else if (arrow.classList.contains("driver-popover-arrow-side-left")) {
        arrow.style.setProperty("border-left-color", "#ffffff", "important");
        const top = Math.max(
          12,
          Math.min(pr.height - 22, er.top + er.height / 2 - pr.top - 5),
        );
        arrow.style.setProperty("top", `${top}px`, "important");
        arrow.style.setProperty("bottom", "auto", "important");
        arrow.style.setProperty("margin-top", "0", "important");
      } else if (arrow.classList.contains("driver-popover-arrow-side-bottom")) {
        arrow.style.setProperty("border-bottom-color", "#ffffff", "important");
        const left = Math.max(
          12,
          Math.min(pr.width - 22, er.left + er.width / 2 - pr.left - 5),
        );
        arrow.style.setProperty("left", `${left}px`, "important");
        arrow.style.setProperty("right", "auto", "important");
        arrow.style.setProperty("margin-left", "0", "important");
      } else if (arrow.classList.contains("driver-popover-arrow-side-top")) {
        arrow.style.setProperty("border-top-color", "#ffffff", "important");
        const left = Math.max(
          12,
          Math.min(pr.width - 22, er.left + er.width / 2 - pr.left - 5),
        );
        arrow.style.setProperty("left", `${left}px`, "important");
        arrow.style.setProperty("right", "auto", "important");
        arrow.style.setProperty("margin-left", "0", "important");
      }
    };

    const driverObj = driver({
      showProgress: true,
      animate: true,
      smoothScroll: true,
      allowClose: true,
      overlayColor: "rgba(0, 0, 0, 0.55)",
      stagePadding: 8,
      stageRadius: 12,
      popoverClass: "pro-tour-popover",
      nextBtnText: t.nextBtn,
      prevBtnText: t.prevBtn,
      doneBtnText: t.doneBtn,
      progressText: t.progressText,
      onPopoverRender: () => requestAnimationFrame(fixArrow),
      onHighlighted: fixArrow,
      onDestroyStarted: () => {
        driverObj.destroy();
        localStorage.setItem(TOUR_STORAGE_KEY, "true");
        // driver.js can leave overflow:hidden on body/html — restore scroll
        document.body.style.overflow = "";
        document.body.style.overflowY = "";
        document.documentElement.style.overflow = "";
        document.documentElement.style.overflowY = "";
      },
      steps: buildSteps(),
    });

    driverRef.current = driverObj;
    driverObj.drive();
    // Mark as seen once the tour actually starts so it only auto-plays once
    // (even if the user doesn't finish it).
    localStorage.setItem(TOUR_STORAGE_KEY, "true");
  }, [buildSteps, t]);

  // Expose startTour globally so the floating "?" button can call it
  useEffect(() => {
    window.__startProTour = startTour;
    return () => {
      delete window.__startProTour;
    };
  }, [startTour]);

  // Auto-start on first visit — guard with ref so language changes don't retrigger
  const hasAutoStarted = useRef(false);
  useEffect(() => {
    if (!isReady || hasAutoStarted.current) return;
    if (localStorage.getItem(TOUR_STORAGE_KEY)) {
      hasAutoStarted.current = true;
      return;
    }
    hasAutoStarted.current = true;
    const timer = setTimeout(startTour, 800);
    return () => clearTimeout(timer);
  }, [isReady, startTour]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (driverRef.current) {
        driverRef.current.destroy();
      }
    };
  }, []);

  return null;
};

export default AppTour;
